import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../components/ui/Button';
import { useUpdateMedia, useUpdatePlaylistContentSettings } from '../../../queries';
import { readImageFit } from '../../../utils/imageFit';
import { handleRssSetting, trimFileNameExc } from '../../../config/global';
import { setRssSettings } from '../../../redux/Slices/Apps/appsSlice';
import RssSettings from '../RSS/RssSettings';
import MediaThumb from './components/MediaThumb';
import CornerAreaControl from './components/CornerAreaControl';

const FITS = ['fit', 'crop', 'fill'];
const RSS_TYPES = ['RSS', 'RSS_CUSTOM'];

/**
 * Standalone-playlist-page item settings sheet — restyles
 * MediaSettingPopup.jsx's fields into the prototype's `.pp-cb-sheet`/
 * `.pp-cb-sgroup`/`.pp-cb-srow`/`.pp-cb-sw`/`.pp-cb-fitbox` pattern, the SAME
 * way BuilderItemSettingsSheet.jsx already did for the campaign builder
 * (own component, MediaSettingPopup.jsx itself untouched — every other
 * caller of it keeps working exactly as before).
 *
 * Saves through the SAME hooks/endpoints MediaSettingPopup.jsx already uses
 * for a playlist row: useUpdatePlaylistContentSettings (POST
 * /playlist/media/setting) for mute/image_fit/corner settings, useUpdateMedia
 * (POST /media/{id}/update) for the file-name rename. CornerAreaControl and
 * RssSettings are reused completely unchanged, just wrapped in
 * `.pp-cb-sgroup` containers.
 *
 * Per the build spec: NO downloadable toggle (platform decision, scoped to
 * this sheet only — MediaSettingPopup.jsx itself still has it for its other
 * callers). Overlay-layer toggles stay suppressed here exactly as they are
 * for every non-campaign caller today (that gate — `layer === "MAIN" &&
 * pathname.includes("campaign")` — is never true on `/playlist/:id/show`,
 * so this sheet never renders them; not reimplemented here at all).
 */
function PlaylistItemSettingsSheet({ item, layer, playlistId, onClose }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { formState: { errors }, control, reset } = useForm();
  const { rssSettings } = useSelector((state) => state.apps);

  const updatePlaylistItemSetting = useUpdatePlaylistContentSettings();
  const updateMediaName = useUpdateMedia();

  const [show, setShow] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setShow(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const _settings = handleRssSetting(item?.rss_settings, item?.title);
    dispatch(setRssSettings(_settings));
    reset(_settings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [name, setName] = useState(trimFileNameExc(item.file_name));
  const [editName, setEditName] = useState(false);
  const [setting, setSetting] = useState({
    is_muted: !!item.is_muted,
    image_fit: readImageFit(item) || 'crop',
    maintain_aspect_ratio: !!item.maintain_aspect_ratio,
  });
  // ⚠️ GET /playlist/{id}/{layer}/media (PlaylistMediaController::
  // getPlaylistMedia()) returns this pivot's JSON column as plain `settings`
  // — it is NEVER aliased to `media_settings` the way the CAMPAIGN endpoint
  // aliases its own. `readImageFit()` below already falls back across both
  // field names for exactly this reason; mirrored here so a playlist row's
  // CORNER-area control doesn't silently initialize blank every time it's
  // reopened (confirmed no playlist row in this company has `settings` set
  // yet — verified live, not assumed — so this was previously unexercised,
  // not previously working).
  const [cornerMediaSettings, setCornerMediaSettings] = useState(item.media_settings ?? item.settings);

  const isRss = RSS_TYPES.includes(item.type);
  const isCornerLayer = (layer === 'CORNER' || layer === 'PLAYGROUND') && (item.type === 'IMAGE' || item.type === 'CORNER');
  const canFit = item.type === 'IMAGE' || item.type === 'VIDEO';

  const close = () => {
    setShow(false);
    setTimeout(() => onClose?.(), 260);
  };

  const saveName = async () => {
    const trimmed = name.trim();
    if (trimmed && trimmed !== trimFileNameExc(item.file_name)) {
      // ⚠️ No extension appended here (unlike MediaSettingPopup.jsx's
      // hardcoded `${name}.xml`, which only happened to be harmless because
      // MediaController::update() always re-derives the REAL extension from
      // the media's own stored path and overwrites whatever the client
      // sent — see app/Http/Controllers/Company/MediaController.php's
      // update(), confirmed by reading it). Sending the plain trimmed name
      // gets the same correct result without carrying that RSS-only quirk
      // into a field every media type uses here.
      await updateMediaName.mutateAsync({ id: item.media_id, data: { name: trimmed } });
      queryClient.invalidateQueries(['getPlaylistLayerContent', playlistId, layer]);
    }
    setEditName(false);
  };

  const save = async () => {
    const formData = new FormData();
    formData.append('playlist_media_id', item.playlist_media_id);
    formData.append('is_muted', Number(setting.is_muted));
    formData.append('maintain_aspect_ratio', Number(setting.image_fit !== 'fill'));
    if (setting.image_fit) formData.append('image_fit', setting.image_fit);

    // ⚠️ PRE-EXISTING, CONFIRMED LIVE, NOT FIXED HERE (out of scope — the
    // build spec says keep CornerAreaControl's "underlying logic/fields"
    // untouched): MediaRequest::rules() for POST /playlist/media/setting
    // has NO `settings` key at all, so `$request->validated()` silently
    // drops this field server-side — PlaylistMediaController::
    // playlistMediaSetting() only ever writes `settings` through the
    // `image_fit` merge path (MediaDisplaySettings::withImageFit()), never
    // from a raw `settings` payload. Confirmed by a live round-trip write+
    // reread against a real CORNER-layer row on dev: the geometry posted
    // below never persists. This is IDENTICAL to what MediaSettingPopup.jsx/
    // PlaylistSettingPopup.jsx already (silently) do today for this same
    // page — replicated as-is, not newly broken by this redesign.
    if (isCornerLayer) {
      const orderedSettings = {
        width: cornerMediaSettings?.width || 100,
        height: cornerMediaSettings?.height || 100,
        top_margin: cornerMediaSettings?.top_margin || 0,
        start_margin: cornerMediaSettings?.start_margin || 0,
      };
      formData.append('settings', JSON.stringify(orderedSettings));
    }

    if (isRss) {
      const rss_settings = {
        ...rssSettings,
        direction: rssSettings?.direction === 'left' ? 'ltr' : 'rtl',
        font_size: Number(rssSettings?.font_size),
      };
      for (const key in rss_settings) {
        if (key !== 'newSeparator' || rss_settings.newSeparator) {
          formData.append(`rss_settings[${key}]`, rss_settings[key]);
        }
      }
    }

    await updatePlaylistItemSetting.mutateAsync(formData);
    close();
  };

  const itemName = item.file_name || item.title || '';

  return (
    <>
      <div className={`pp-cb-scrim ${show ? 'show' : ''}`} onClick={close}></div>
      <div className={`pp-cb-sheet ${show ? 'show' : ''}`} role="dialog" aria-label={t('show.show_item_sheet_title_media')}>
        <div className="pp-cb-grabber"></div>
        <h3>{t('show.show_item_sheet_title_media')}</h3>
        <div className="pp-cb-sheet-sub">{t('show.show_item_sheet_sub_media')}</div>

        <div className="pp-cb-sheet-body">
          <div className="pp-cb-ihead">
            <div className="pp-cb-item-thumb"><MediaThumb item={item} /></div>
            {editName ? (
              <div style={{ display: 'flex', flex: 1, gap: 6, alignItems: 'center' }}>
                <input
                  className="pp-cb-name-in"
                  value={name}
                  maxLength={80}
                  autoFocus
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') { setName(trimFileNameExc(item.file_name)); setEditName(false); } }}
                />
                <Button label="" icon="fa-solid fa-check" loading={updateMediaName.isLoading} onClick={saveName} />
              </div>
            ) : (
              <div className="n" style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <span className="pp-cb-ihead-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{itemName}</span>
                <button type="button" className="pp-cb-rename" title={t('playlist.rename_tooltip')} onClick={() => setEditName(true)}>
                  <i className="fas fa-pencil" style={{ fontSize: 12 }} />
                </button>
              </div>
            )}
          </div>

          <div className="pp-cb-stitle">{t('show.show_media_settings_title')}</div>
          <div className="pp-cb-sgroup">
            {item.type !== 'IMAGE' && (
              <div className="pp-cb-srow">
                <div className="sl"><div className="a">{t('dragdrop.mute')}</div></div>
                <button
                  type="button"
                  className={`pp-cb-sw ${setting.is_muted ? 'on' : ''}`}
                  role="switch"
                  aria-checked={setting.is_muted}
                  onClick={() => setSetting((p) => ({ ...p, is_muted: !p.is_muted }))}
                />
              </div>
            )}

            {canFit && (
              <div className="pp-cb-fitbox">
                <div className="a">{t('dragdrop.image_fit.label')}</div>
                <div className="pp-cb-seg wide">
                  {FITS.map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      className={setting.image_fit === mode ? 'on' : ''}
                      onClick={() => setSetting((p) => ({ ...p, image_fit: mode, maintain_aspect_ratio: mode !== 'fill' }))}
                    >
                      {t(`dragdrop.image_fit.${mode}`)}
                    </button>
                  ))}
                </div>
                <div className="pp-cb-fit-hint">{t(`show.show_fit_hint_${setting.image_fit}`)}</div>
              </div>
            )}

            {isCornerLayer && (
              <div style={{ padding: '10px 0' }}>
                <CornerAreaControl layer area={cornerMediaSettings} setArea={setCornerMediaSettings} />
              </div>
            )}
          </div>

          {isRss && (
            <>
              <div className="pp-cb-stitle">{t('dragdrop.rss_settings')}</div>
              <div className="pp-cb-sgroup">
                <RssSettings errors={errors} control={control} reset={reset} />
              </div>
            </>
          )}
        </div>

        <div style={{ flex: '0 0 auto' }}>
          <Button label={isRss ? t('buttons.publish') : t('buttons.save')} className="w-100" onClick={save} loading={updatePlaylistItemSetting.isLoading} />
          <Button label={t('buttons.cancel')} outlined className="w-100" style={{ marginTop: 10 }} onClick={close} />
        </div>
      </div>
    </>
  );
}

export default PlaylistItemSettingsSheet;
